import { Dispatch, SetStateAction, useCallback } from 'react';
import { DEFAULT_PROJECT_SETTINGS, MapEntitySources } from 'constants/entities';
import {
  initialOperationalSettings,
  initialQuickFilterSettings,
} from 'constants/localStorage';
import { MAP_SETTINGS } from 'constants/map';
import {
  DefaultProjectSettings,
  IDefaultSourceSettings,
  ILayerQuickFilterSettings,
  IRecentProjectsSettings,
  MapSettingsSettings,
  OperationalSettings,
} from 'interfaces/localStorage';
import { ValueOf } from 'types';
import { useLocalStorage } from 'usehooks-ts';

export const useLocalStorageWrapper = <T extends object>(
  localStorageKey: string,
  initialValue: T
): [
  T,
  (key: keyof T, value: ValueOf<T> | null) => void,
  (prevState: T) => void
] => {
  const [data, setData] = useLocalStorage(localStorageKey, initialValue);

  const updateDatum = useCallback(
    (key: keyof T, value?: ValueOf<T> | null) =>
      setData((prev) => ({ ...prev, [key]: value })),
    []
  );

  return [data, updateDatum, setData];
};

export const useUpdateFunctionForKey = <T extends object, V = ValueOf<T>>(
  key: keyof T,
  updateFunction: (key: keyof T, value: V | null) => void,
  currentValue: V
): Dispatch<SetStateAction<V>> => {
  return useCallback(
    (payload: SetStateAction<V>) => {
      if (payload instanceof Function) {
        updateFunction(key, payload(currentValue));
      } else {
        updateFunction(key, payload);
      }
    },
    [updateFunction, currentValue]
  );
};

export const useMapSettingsLocalStorage = (
  initialValue: MapSettingsSettings
): [MapSettingsSettings, (key: string, value?: boolean | null) => void] => {
  const [value, setValue] = useLocalStorage(MAP_SETTINGS, initialValue);

  const updateValue = useCallback(
    (key: string, value?: boolean | null) =>
      setValue((prev) => ({ ...prev, [key]: value })),
    []
  );

  return [value, updateValue];
};

export const useOperationalLocalStorage = (
  initialValue: OperationalSettings = initialOperationalSettings
) => useLocalStorage('operational_settings', initialValue);

export const useDefaultProjectSettingsLocalStorage = (
  initialValue?: DefaultProjectSettings
): [
  DefaultProjectSettings | undefined,
  (value?: string | null) => void,
  (value?: string | null) => void
] => {
  const storedValue = localStorage.getItem(DEFAULT_PROJECT_SETTINGS);
  const value: DefaultProjectSettings | undefined =
    storedValue && JSON.parse(storedValue);

  const [data, setData] = useLocalStorage<DefaultProjectSettings | undefined>(
    DEFAULT_PROJECT_SETTINGS,
    value || initialValue
  );

  const updateData = useCallback(
    (key: string, value?: string | null) =>
      setData((prev) => ({ ...prev, [key]: value })),
    []
  );

  const updateProjectId = useCallback(
    (value?: string | null) => updateData('projectId', value),
    [updateData]
  );

  const updateSubprojectType = useCallback(
    (value?: string | null) => updateData('subProjectType', value),
    [updateData]
  );

  return [data, updateProjectId, updateSubprojectType];
};

export const useLayerQuickFilterLocalStorage = (
  initialValue: ILayerQuickFilterSettings = initialQuickFilterSettings
) => useLocalStorage('layer_filters', initialValue);

export const useRecentProjectsLocalStorage = (
  initialValue: IRecentProjectsSettings = { recentProjects: [] }
): [
  recentProjects: number[],
  upsertRecentProject: (latestProject: number) => void,
  setRecentProjects: Dispatch<SetStateAction<number[]>>
] => {
  const [data, updateDatum] = useLocalStorageWrapper(
    'recent_projects',
    initialValue
  );

  const setRecentProjects = useUpdateFunctionForKey(
    'recentProjects',
    updateDatum,
    data.recentProjects
  );

  const upsertRecentProject = useCallback(
    (latestProject: number) =>
      setRecentProjects((prevState) => {
        if (prevState.includes(latestProject)) {
          return [
            latestProject,
            ...prevState.flatMap((projectId) =>
              projectId === latestProject ? [] : projectId
            ),
          ];
        } else {
          return [latestProject, ...prevState.slice(0, 2)];
        }
      }),
    [setRecentProjects]
  );

  return [data.recentProjects, upsertRecentProject, setRecentProjects];
};

export const useDefaultSourceLocalStorage = (
  providedInitialValue?: IDefaultSourceSettings
): [
  defaultSource: MapEntitySources,
  updateDefaultSources: Dispatch<SetStateAction<MapEntitySources>>
] => {
  const initialValue = providedInitialValue
    ? providedInitialValue
    : {
        defaultSource: MapEntitySources.VR,
      };

  const [data, updateDatum] = useLocalStorageWrapper(
    'default_source',
    initialValue
  );
  const updateDefaultSource = useUpdateFunctionForKey(
    'defaultSource',
    updateDatum,
    data.defaultSource
  );

  return [data.defaultSource, updateDefaultSource];
};
